import { Clock, MapPin, Phone, Instagram, Info, ArrowUpRight } from "lucide-react";
import Reveal, { SectionHeader } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import {
    HOURS,
    ADDRESS,
    ADDRESS_CITY,
    MAPS_LINK,
    PHONE_DISPLAY,
    PHONE_LINK,
    WHATSAPP_DEFAULT,
    WHATSAPP_DISPLAY,
    INSTAGRAM_HANDLE,
    getOpenStatus,
} from "@/lib/site";

function OpenBadge() {
    const status = getOpenStatus();
    return (
        <span
            data-testid="open-status-badge"
            className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold px-3.5 py-1.5 rounded-full border ${
                status.open
                    ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/5"
                    : "text-red-400/90 border-red-400/25 bg-red-400/5"
            }`}
        >
            <span className="relative flex h-2 w-2">
                {status.open && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                )}
                <span
                    className={`relative inline-flex rounded-full h-2 w-2 ${
                        status.open ? "bg-emerald-400" : "bg-red-400/80"
                    }`}
                />
            </span>
            {status.label}
        </span>
    );
}

export default function Contact() {
    return (
        <section
            id="contato"
            data-testid="contact-location-section"
            className="py-20 sm:py-28 lg:py-32 bg-charcoal border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <SectionHeader
                    eyebrow="Horários & Localização"
                    title="Visite a Casa"
                    subtitle="Agende pelo WhatsApp ou apareça — a cadeira é sua, o ritual é nosso."
                />

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    <Reveal className="h-full">
                        <div className="h-full rounded-2xl bg-card border border-white/8 hover:border-gold/40 transition-colors duration-300 p-7 sm:p-9">
                            <div className="flex items-center justify-between gap-4 flex-wrap mb-7">
                                <div className="flex items-center gap-3">
                                    <span className="flex items-center justify-center w-11 h-11 rounded-xl border border-gold/35 text-gold">
                                        <Clock size={20} />
                                    </span>
                                    <h3 className="font-display font-bold uppercase text-lg tracking-wide text-white">
                                        Horários de Funcionamento
                                    </h3>
                                </div>
                                <OpenBadge />
                            </div>

                            <div data-testid="contact-hours-list">
                                {HOURS.map((h) => (
                                    <div
                                        key={h.days}
                                        className="flex items-baseline justify-between gap-4 py-4 border-b border-white/5 last:border-0"
                                    >
                                        <div>
                                            <span className="font-semibold text-cream text-[15px]">
                                                {h.days}
                                            </span>
                                            {h.note && (
                                                <span className="block text-xs text-gold/70 mt-0.5">
                                                    {h.note}
                                                </span>
                                            )}
                                        </div>
                                        <span
                                            className={`font-mono text-sm whitespace-nowrap ${
                                                h.time === "Fechado" ? "text-red-400/80" : "text-gold"
                                            }`}
                                        >
                                            {h.time}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-7 flex items-start gap-2.5 text-xs text-cream/45 leading-relaxed">
                                <Info size={14} className="shrink-0 mt-0.5 text-gold/60" />
                                Horários fictícios — conteúdo meramente ilustrativo deste site de demonstração.
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12} className="h-full">
                        <div className="h-full rounded-2xl bg-card border border-white/8 hover:border-gold/40 transition-colors duration-300 p-7 sm:p-9 flex flex-col">
                            <div className="flex items-center gap-3 mb-7">
                                <span className="flex items-center justify-center w-11 h-11 rounded-xl border border-gold/35 text-gold">
                                    <MapPin size={20} />
                                </span>
                                <h3 className="font-display font-bold uppercase text-lg tracking-wide text-white">
                                    Onde Estamos
                                </h3>
                            </div>

                            <div
                                data-testid="contact-address-card"
                                className="rounded-xl border border-gold/20 bg-obsidian/60 p-6"
                            >
                                <p className="font-semibold text-cream">{ADDRESS}</p>
                                <p className="text-cream/60 text-sm mt-1">{ADDRESS_CITY}</p>
                                <a
                                    href={MAPS_LINK}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-hover transition-colors"
                                >
                                    Como chegar
                                    <ArrowUpRight size={14} />
                                </a>
                            </div>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <a
                                    href={PHONE_LINK}
                                    data-testid="contact-phone-link"
                                    className="group flex items-center gap-3.5 rounded-xl border border-white/8 hover:border-gold/40 transition-colors p-4"
                                >
                                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gold/10 text-gold shrink-0">
                                        <Phone size={17} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-cream/45">
                                            Telefone
                                        </span>
                                        <span className="block font-mono text-sm text-cream truncate">
                                            {PHONE_DISPLAY}
                                        </span>
                                    </span>
                                </a>

                                <div className="flex items-center gap-3.5 rounded-xl border border-white/8 p-4 cursor-default">
                                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gold/10 text-gold shrink-0">
                                        <Instagram size={17} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-[10px] uppercase tracking-[0.2em] text-cream/45">
                                            Instagram
                                        </span>
                                        <span className="block font-mono text-sm text-cream truncate">
                                            {INSTAGRAM_HANDLE}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <a
                                href={WHATSAPP_DEFAULT}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-whatsapp-link"
                                className="mt-6 inline-flex items-center justify-center gap-3 bg-wa hover:bg-[#1EBE5B] text-white font-semibold uppercase tracking-[0.1em] text-sm px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_10px_35px_rgba(37,211,102,0.35)]"
                            >
                                <WhatsAppIcon size={19} />
                                Chamar no WhatsApp
                            </a>
                            <p className="mt-3 text-center font-mono text-xs text-cream/45">
                                {WHATSAPP_DISPLAY} — número fictício de demonstração
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
